/**
 * API Documentation Service
 * Uses local swagger files for development and APIM endpoints for production
 * Automatically detects enviroment and uses appropriate source
 */

import logger from 'utils/logger';

class ApiDocumentationService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.isLocalDevelopment = this.detectLocalDevelopment();
    this.baseUrl = null;
    this.initPromise = this.initialize();
    
    // Allowed public APIs (filter out deprecated)
    this.ALLOWED_PUBLIC = new Set([
      'agent-actions-api',
      'bot-operations-api', 
      'bot-management-api',
      'knowledge-management-api',
      'public-data-api'
    ]);
    
    // Display name overrides for API Groups
    this.DISPLAY_NAME_OVERRIDES = {
      'Security Groups': 'Access Control',
      'User Api': 'Users',
      'App User Api': 'Application Users', 
      'Resource Api': 'Resources',
      'Tenant Api': 'Tenants',
      'Bot Management': 'Bots',
      'Knowledge Management': 'Knowledge',
      'Cost Pool Api': 'Cost Pools'
    };
  }

  async initialize() {
    if (this.isLocalDevelopment) {
      // Using local swagger files
    } else {
      // Using static swagger files from React site - no baseUrl needed
      this.baseUrl = null;
    }
    return this.baseUrl;
  }

  async ensureInitialized() {
    // No initialization needed for static files
    // This method is kept for compatibility but does nothing
  }

  /**
   * Detect if running in local development enviroment
   * @returns {boolean} True if running locally
   */
  detectLocalDevelopment() {
    // Check if running on localhost or in development mode
    return (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('local') ||
      process.env.NODE_ENV === 'development'
    );
  }

  /**
   * Check if a tag should be hidden from UI
   * @param {string|object} tag - Tag name or tag object
   * @returns {boolean} True if tag should be hidden
   */
  isHiddenTag(tag) {
    if (!tag) return false;
    const name = typeof tag === 'string' ? tag : tag.name;
    // Hide CORS/Preflight groups
    return (typeof tag === 'object' && tag['x-hidden'] === true) || 
           /^(CORS|Preflight)/i.test(name) || 
           /preflight/i.test(name);
  }

  /**
   * Get display name for a tag
   * @param {string|object} tag - Tag name or tag object
   * @returns {string} Display name
   */
  getDisplayName(tag) {
    if (typeof tag === 'object' && tag['x-display-name']) {
      return tag['x-display-name'];
    }
    const name = typeof tag === 'string' ? tag : tag.name;
    return this.DISPLAY_NAME_OVERRIDES[name] || name;
  }

  /**
   * Get API documentation for a specific API
   * @param {string} apiName - The API name (e.g., 'demo-bot', 'bot-operations')
   * @returns {Promise<Object>} The swagger/OpenAPI specification
   */
  async getApiDocumentation(apiName) {
    const cacheKey = `api-docs-${apiName}`;
    const cached = this.cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }

    try {
      if (this.isLocalDevelopment) {
        // Use local swagger files for development
        return await this.getLocalSwaggerDocumentation(apiName);
      } else {
        // Use static swagger files from React site
        return await this.getStaticSwaggerDocumentation(apiName);
      }
    } catch (error) {
      logger.error('API-DOC-SERVICE', 'Error loading API documentation', { apiName, error: error.message });
      // Return fallback documentation instead of throwing
      return this.getFallbackApiDocumentation(apiName);
    }
  }

  /**
   * Get swagger documentation from local files
   * @param {string} apiName - The API name
   * @returns {Promise<Object>} The swagger specification
   */
  async getLocalSwaggerDocumentation(apiName) {
    // Swagger API calls disabled - no longer fetching /swagger/*.json files
    logger.warn('API-DOC-SERVICE', 'Swagger API calls disabled, using fallback documentation', { apiName });
    return this.getFallbackApiDocumentation(apiName);
  }

  /**
   * Get swagger documentation from static files in React site
   * @param {string} apiName - The API name
   * @returns {Promise<Object>} The swagger specification
   */
  async getStaticSwaggerDocumentation(apiName) {
    // Map API name to portal
    // eslint-disable-next-line no-unused-vars
    const portalMap = {
      'demo': 'public',
      'chat': 'public',
      'bot-management': 'public',
      'administrative': 'public',
      'bot-operations': 'public',
      'knowledge-management': 'public',
      'support': 'public',
      'notifications': 'public',
      'customer-outreach': 'public',
      'subscription-management': 'public',
      'payment': 'public',
      'besper-site': 'public',
      'admin': 'public',
      // Customer portal APIs (v1 module paths)
      'resources': 'v1',
      'tenants': 'v1',
      'users': 'v1',
      'cost-pools': 'v1',
      'workspaces': 'v1',
      'security-groups': 'v1'
    };
    
    // Swagger API calls disabled - no longer fetching /swagger/*.json files
    logger.warn('API-DOC-SERVICE', 'Swagger API calls disabled, using fallback documentation', { apiName });
    return this.getFallbackApiDocumentation(apiName);
  }

  /**
   * Filter swagger data by API name (for portals with multiple APIs)
   * @param {Object} swaggerData - Full swagger specification
   * @param {string} apiName - The API name to filter by
   * @returns {Object} Filtered swagger specification
   */
  filterSwaggerByApiName(swaggerData, apiName) {
    // If the swagger data contains tags or paths specific to the API name, filter them
    // For now, return the full swagger data as portals may contain multiple APIs
    // This can be enhanced later if needed
    return swaggerData;
  }

  /**
   * Get APIM API ID from service name
   * @param {string} apiName - The API name
   * @returns {string} The APIM API ID
   */
  getApiId(apiName) {
    const apiIdMapping = {
      'demo': 'demo-bot-api',
      'chat': 'demo-bot-api',
      'bot-management': 'bot-management-api',
      'administrative': 'administrative-api'
    };
    
    return apiIdMapping[apiName] || apiName;
  }

  /**
   * Get fallback API documentation when APIM endpoints are not accessible
   * @param {string} apiName - The API name
   * @returns {Object} Fallback API documentation
   */
  getFallbackApiDocumentation(apiName) {
    const fallbackDocs = {
      'demo': {
        name: 'Chat API',
        description: 'Unified chat API for bot operations, session management, and AI response generation',
        operations: {
          'create_bot': {
            description: 'Create a new bot (demo or operational)',
            endpoint: '/create_bot',
            method: 'POST',
            parameters: ['name', 'description', 'tenant_id'],
            example: {
              name: 'My Demo Bot',
              description: 'A demo bot for testing',
              tenant_id: 'tenant-123'
            }
          },
          'list_demo_bots': {
            description: 'List all demo bots',
            endpoint: '/api/demo-bot/list',
            method: 'GET',
            parameters: ['tenant_id'],
            example: {}
          }
        }
      },
      'chat': {
        name: 'Chat API',
        description: 'Unified chat API for bot operations, session management, and AI response generation',
        operations: {
          'create_bot': {
            description: 'Create a new bot (demo or operational)',
            endpoint: '/create_bot',
            method: 'POST',
            parameters: ['bot_config'],
            example: {
              bot_config: {
                name: 'My Bot',
                description: 'A bot for testing'
              }
            }
          }
        }
      },
      'bot-management': {
        name: 'Bot Management API',
        description: 'API for comprehensive bot management',
        operations: {
          'manage_bot': {
            description: 'Manage bot settings and configuration',
            endpoint: '/api/v1/bot-management/manage',
            method: 'POST',
            parameters: ['bot_id', 'settings'],
            example: {
              bot_id: 'bot-123',
              settings: {}
            }
          }
        }
      },
      'administrative': {
        name: 'Administrative API',
        description: 'API for administrative operations',
        operations: {
          'admin_operation': {
            description: 'Perform administrative operation',
            endpoint: '/api/administrative/operation',
            method: 'POST',
            parameters: ['operation_type'],
            example: {
              operation_type: 'system_check'
            }
          }
        }
      }
    };

    return fallbackDocs[apiName] || {
      name: `${apiName} API`,
      description: `API for ${apiName} operations`,
      operations: {
        'default_operation': {
          description: 'Default operation',
          endpoint: `/api/${apiName}/operation`,
          method: 'POST',
          parameters: [],
          example: {}
        }
      }
    };
  }

  /**
   * Get all available API documentation
   * @returns {Promise<Object>} Combined API definitions
   */
  async getAllApiDocumentation() {
    const apiNames = [
      'demo',
      'chat', 
      'bot-management',
      'administrative'
    ];

    const apiDefinitions = {};

    for (const apiName of apiNames) {
      try {
        const swaggerData = await this.getApiDocumentation(apiName);
        const processedApi = this.processSwaggerData(swaggerData, apiName);
        apiDefinitions[apiName] = processedApi;
      } catch (error) {
        logger.warn('API-DOC-SERVICE', 'Failed to load API documentation, using fallback', { apiName, error: error.message });
        // Use fallback documentation instead of throwing
        const fallbackData = this.getFallbackApiDocumentation(apiName);
        apiDefinitions[apiName] = fallbackData;
      }
    }

    return apiDefinitions;
  }

  /**
   * Get documentation model for docs page (both public and customer-portal)
   * @returns {Promise<Object>} Documentation model with scopes, tags, and operations
   */
  async getDocumentationModel() {
    try {
      // Swagger API calls disabled - no longer fetching /swagger/index.json
      // Return empty documentation model
      const indexData = { apis: [] };
      
      const docsModel = {
        public: { tags: [], operations: [] },
        'customer-portal': { tags: [], operations: [] }
      };

      // Get base URLs for code examples
      const { getApiEndpoint } = require('../utils/centralizedConfig');
      const publicBaseUrl = await getApiEndpoint('public');
      const customerPortalBaseUrl = await getApiEndpoint('customer-portal');

      // Load each API and build docs model
      for (const api of indexData.apis) {
        if (!api.available) continue;
        
        // Filter out deprecated public APIs
        if (api.scope === 'public' && !this.ALLOWED_PUBLIC.has(api.name)) {
          logger.debug('API-DOC-SERVICE', 'Skipping deprecated public API', { apiName: api.name });
          continue;
        }
        
        try {
          logger.debug('API-DOC-SERVICE', 'Loading API', { apiName: api.name, scope: api.scope });
          const swaggerData = await this.getApiDocumentation(api.name);
          const scope = api.scope || 'public';
          const baseUrl = scope === 'customer-portal' ? customerPortalBaseUrl : publicBaseUrl;
          logger.debug('API-DOC-SERVICE', 'Loaded swagger data', { apiName: api.name });
          
          // Extract tags and operations
          if (swaggerData.tags) {
            for (const tag of swaggerData.tags) {
              // Skip hidden tags
              if (this.isHiddenTag(tag)) {
                logger.debug('API-DOC-SERVICE', 'Skipping hidden tag', { tagName: tag.name || tag });
                continue;
              }
              
              const displayName = this.getDisplayName(tag);
              const existingTag = docsModel[scope].tags.find(t => t.name === displayName);
              if (!existingTag) {
                docsModel[scope].tags.push({
                  name: displayName,
                  description: tag.description || '',
                  order: tag['x-order'] || 999
                });
              }
            }
          }

          // Extract operations with code examples
          if (swaggerData.paths) {
            for (const [path, pathData] of Object.entries(swaggerData.paths)) {
              for (const [method, operationData] of Object.entries(pathData)) {
                if (method === 'options') continue;
                
                if (operationData.operationId) {
                  // If no tags are defined, create a default tag based on the API name
                  const defaultTag = api.name.replace(/^v1-/, '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  const rawOperationTags = operationData.tags && operationData.tags.length > 0 ? operationData.tags : [defaultTag];
                  
                  // Filter out hidden tags and normalize display names
                  const operationTags = rawOperationTags
                    .filter(tag => !this.isHiddenTag(tag))
                    .map(tag => this.getDisplayName(tag));
                  
                  // Skip operation if all tags are hidden
                  if (operationTags.length === 0) {
                    logger.debug('API-DOC-SERVICE', 'Skipping operation - all tags hidden', { operationId: operationData.operationId });
                    continue;
                  }
                  
                  const operation = {
                    operationId: operationData.operationId,
                    method: method.toUpperCase(),
                    path: path,
                    summary: operationData.summary || operationData.operationId,
                    description: operationData.description || '',
                    tags: operationTags,
                    order: operationData['x-order'] || 999,
                    scope: scope,
                    codeExamples: this.generateCodeExamples(operationData, method, path, baseUrl, scope)
                  };
                  
                  docsModel[scope].operations.push(operation);
                  logger.debug('API-DOC-SERVICE', 'Added operation', { operationId: operation.operationId, scope });
                  
                  // Add the tag to the tags list if it doesn't exist
                  const existingTag = docsModel[scope].tags.find(t => t.name === defaultTag);
                  if (!existingTag) {
                    docsModel[scope].tags.push({
                      name: defaultTag,
                      description: `${defaultTag} operations`,
                      order: 999
                    });
                    logger.debug('API-DOC-SERVICE', 'Added tag', { tagName: defaultTag, scope });
                  }
                }
              }
            }
          }
        } catch (error) {
          logger.warn('API-DOC-SERVICE', 'Failed to load API', { apiName: api.name, error: error.message });
        }
      }

      // Sort tags and operations by order
      for (const scope of Object.keys(docsModel)) {
        docsModel[scope].tags.sort((a, b) => a.order - b.order);
        docsModel[scope].operations.sort((a, b) => a.order - b.order);
        logger.debug('API-DOC-SERVICE', 'Final scope summary', {
          scope,
          tagsCount: docsModel[scope].tags.length,
          operationsCount: docsModel[scope].operations.length
        });
      }

      logger.info('API-DOC-SERVICE', 'Final docs model built', {
        publicTags: docsModel.public.tags.length,
        publicOperations: docsModel.public.operations.length,
        customerPortalTags: docsModel['customer-portal'].tags.length,
        customerPortalOperations: docsModel['customer-portal'].operations.length
      });
      return docsModel;
    } catch (error) {
      logger.error('API-DOC-SERVICE', 'Failed to build documentation model', { error: error.message });
      return {
        public: { tags: [], operations: [] },
        'customer-portal': { tags: [], operations: [] }
      };
    }
  }

  /**
   * Generate code examples for an operation
   * @param {Object} operationData - The operation data from swagger
   * @param {string} method - HTTP method
   * @param {string} path - API path
   * @param {string} baseUrl - Base URL for the API
   * @param {string} scope - API scope (public or customer-portal)
   * @returns {Object} Code examples in different formats
   */
  generateCodeExamples(operationData, method, path, baseUrl, scope) {
    const fullUrl = `${baseUrl}${path}`;
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase());
    
    // Generate example body if available
    let exampleBody = {};
    if (hasBody && operationData.requestBody) {
      const content = operationData.requestBody.content;
      if (content && content['application/json'] && content['application/json'].schema) {
        exampleBody = this.generateExampleFromSchema(content['application/json'].schema);
      }
    }

    // Generate headers based on scope
    const headers = this.generateHeaders(scope);
    
    return {
      curl: this.generateCurlExample(method, fullUrl, headers, exampleBody),
      javascript: this.generateJavaScriptExample(method, fullUrl, headers, exampleBody),
      python: this.generatePythonExample(method, fullUrl, headers, exampleBody)
    };
  }

  /**
   * Generate headers based on API scope
   * @param {string} scope - API scope
   * @returns {Object} Headers object
   */
  generateHeaders(scope) {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (scope === 'customer-portal') {
      headers['Authorization'] = 'Bearer <YOUR_SECRET>';
    } else {
      headers['X-API-Key'] = '<YOUR_API_KEY>';
    }

    return headers;
  }

  /**
   * Generate curl example
   */
  generateCurlExample(method, url, headers, body) {
    let curl = `curl -X ${method.toUpperCase()} "${url}"`;
    
    Object.entries(headers).forEach(([key, value]) => {
      curl += ` \\\n  -H "${key}: ${value}"`;
    });

    if (Object.keys(body).length > 0) {
      curl += ` \\\n  -d '${JSON.stringify(body, null, 2)}'`;
    }

    return curl;
  }

  /**
   * Generate JavaScript fetch example
   */
  generateJavaScriptExample(method, url, headers, body) {
    const hasBody = Object.keys(body).length > 0;
    
    return `fetch('${url}', {
  method: '${method.toUpperCase()}',
  headers: ${JSON.stringify(headers, null, 2)},
${hasBody ? `  body: JSON.stringify(${JSON.stringify(body, null, 2)})` : ''}
})
.then(response => response.json())
.then(data => console.log(data))
.catch(error => console.error('Error:', error));`;
  }

  /**
   * Generate Python requests example
   */
  generatePythonExample(method, url, headers, body) {
    const hasBody = Object.keys(body).length > 0;
    
    return `import requests

url = '${url}'
headers = ${JSON.stringify(headers, null, 2)}
${hasBody ? `data = ${JSON.stringify(body, null, 2)}` : ''}

response = requests.${method.toLowerCase()}(
    url,
    headers=headers${hasBody ? ',\n    json=data' : ''}
)

print(response.json())`;
  }

  /**
   * Generate example from JSON schema
   */
  generateExampleFromSchema(schema) {
    if (schema.example) {
      return schema.example;
    }

    if (schema.properties) {
      const example = {};
      Object.entries(schema.properties).forEach(([key, prop]) => {
        if (prop.type === 'string') {
          example[key] = `example_${key}`;
        } else if (prop.type === 'number') {
          example[key] = 0;
        } else if (prop.type === 'boolean') {
          example[key] = true;
        } else if (prop.type === 'array') {
          example[key] = [];
        } else if (prop.type === 'object') {
          example[key] = {};
        }
      });
      return example;
    }

    return {};
  }

  /**
   * Process swagger data into our API definition format
   * @param {Object} swaggerData - The swagger/OpenAPI data
   * @param {string} apiName - The API name
   * @returns {Object} Processed API definition
   */
  processSwaggerData(swaggerData, apiName) {
    const operations = {};
    
    if (swaggerData.paths) {
      Object.entries(swaggerData.paths).forEach(([path, pathData]) => {
        Object.entries(pathData).forEach(([method, operationData]) => {
          if (method === 'options') return; // Skip CORS preflight
          
          if (operationData.operationId) {
            const operationName = operationData.operationId;
            const operation = {
              description: operationData.summary || operationData.description || operationName,
              endpoint: path,
              method: method.toUpperCase(),
              parameters: this.extractParameters(operationData),
              example: this.generateExample(operationData)
            };
            
            operations[operationName] = operation;
          }
        });
      });
    }

    return {
      name: swaggerData.info?.title || apiName,
      description: swaggerData.info?.description || `API for ${apiName}`,
      operations: operations
    };
  }

  /**
   * Extract parameters from swagger operation
   * @param {Object} operationData - The operation data
   * @returns {Array} Array of parameter names
   */
  extractParameters(operationData) {
    const parameters = [];
    
    // Extract from requestBody schema
    if (operationData.requestBody?.content?.['application/json']?.schema?.properties) {
      const properties = operationData.requestBody.content['application/json'].schema.properties;
      parameters.push(...Object.keys(properties));
    }
    
    // Extract from parameters
    if (operationData.parameters) {
      operationData.parameters.forEach(param => {
        if (param.name && !parameters.includes(param.name)) {
          parameters.push(param.name);
        }
      });
    }
    
    return parameters;
  }

  /**
   * Generate example data from swagger operation
   * @param {Object} operationData - The operation data
   * @returns {Object} Example data object
   */
  generateExample(operationData) {
    const example = {};
    
    if (operationData.requestBody?.content?.['application/json']?.schema?.properties) {
      const properties = operationData.requestBody.content['application/json'].schema.properties;
      Object.entries(properties).forEach(([key, value]) => {
        example[key] = this.generateExampleValue(value);
      });
    }
    
    return example;
  }

  /**
   * Generate example value based on schema type
   * @param {Object} schema - The schema definition
   * @returns {*} Example value
   */
  generateExampleValue(schema) {
    if (schema.type === 'string') {
      if (schema.enum) return schema.enum[0];
      if (schema.format === 'email') return 'user@example.com';
      if (schema.format === 'date-time') return new Date().toISOString();
      return 'example';
    }
    if (schema.type === 'number' || schema.type === 'integer') return 123;
    if (schema.type === 'boolean') return true;
    if (schema.type === 'array') return [];
    if (schema.type === 'object') return {};
    return null;
  }


  /**
   * Clear the cache
   */
  clearCache() {
    this.cache.clear();
  }
}

const apiDocumentationService = new ApiDocumentationService();
export default apiDocumentationService;
